// src/pages/RepGroupPage.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UnifiedPageWrapper from "../components/Layout/UnifiedPageWrapper";
import SmartTable from "../components/Layout/SmartTable";
import API from "../utils/api";

export default function RepGroupPage() {
  const [reps, setReps] = useState([]);
  const [selectedRep, setSelectedRep] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);

  // 🔹 Fetch all reps
  const fetchReps = async () => {
    try {
      const res = await API.get("/users/reps/");
      setReps(res.data);
    } catch (err) {
      console.error("Error fetching reps:", err.response?.data || err.message);
    }
  };

  // 🔹 Save rep (create or update)
  const handleSaveRep = async () => {
    try {
      if (selectedRep.id) {
        await API.put(`/users/${selectedRep.id}/`, selectedRep);
      } else {
        await API.post("/users/", selectedRep);
      }
      setOpenEdit(false);
      setSelectedRep(null);
      await fetchReps();
    } catch (err) {
      console.error("Error saving rep:", err.response?.data || err.message);
    }
  };

  // 🔹 Delete rep
  const handleDeleteRep = async (id) => {
    try {
      await API.delete(`/users/${id}/`);
      await fetchReps();
    } catch (err) {
      console.error("Error deleting rep:", err);
    }
  };

  // 🔹 Open modal
  const handleRowClick = (params) => {
    setSelectedRep(params.row);
    setOpenEdit(true);
  };

  useEffect(() => {
    fetchReps();
  }, []);

  // 🔹 Table columns
  const columns = [
    { field: "username", headerName: "Username", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.5 },
    {
      field: "is_active",
      headerName: "Status",
      flex: 0.8,
      renderCell: (params) =>
        params.row.is_active ? (
          <span style={{ color: "green", fontWeight: 500 }}>✅ Active</span>
        ) : (
          <span style={{ color: "red", fontWeight: 500 }}>⛔ Inactive</span>
        ),
    },
  ];

  return (
    <UnifiedPageWrapper
      title="Rep Group"
      actionButton={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedRep({ username: "", email: "", is_active: true });
            setOpenEdit(true);
          }}
          sx={{ borderRadius: "8px", textTransform: "none" }}
        >
          Add Rep
        </Button>
      }
    >
      <SmartTable
        rows={reps}
        columns={columns}
        onRowClick={handleRowClick}
        key={JSON.stringify(reps.map((r) => `${r.id}-${r.is_active}`))}
      />

      {/* ✳️ Modal */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedRep?.id ? "Edit Rep" : "New Rep"}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Username"
              value={selectedRep?.username || ""}
              onChange={(e) =>
                setSelectedRep({ ...selectedRep, username: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Email"
              value={selectedRep?.email || ""}
              onChange={(e) =>
                setSelectedRep({ ...selectedRep, email: e.target.value })
              }
              fullWidth
            />
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography>Active</Typography>
              <Switch
                checked={selectedRep?.is_active || false}
                onChange={(e) =>
                  setSelectedRep({
                    ...selectedRep,
                    is_active: e.target.checked,
                  })
                }
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {selectedRep?.id && (
            <Button color="error" onClick={() => handleDeleteRep(selectedRep.id)}>
              Delete
            </Button>
          )}
          <Box flexGrow={1} />
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRep}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </UnifiedPageWrapper>
  );
}
